import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button, Tooltip } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import { SECTION_LINK_REGEX } from '../config/parser';
import { tr } from '../config/strings';
import { PlaybookDoc, PlaybookLang, PlaybookSection } from '../config/types';
import { anchorDomId } from '../config/anchor';
import styles from './PlaybookContent.module.scss';

const SECTION_HREF_PREFIX = '#pb:';

/** Turns `[section-id]` written in the text into a markdown link named after the target section. */
const linkifySectionRefs = (body: string, doc: PlaybookDoc): string =>
  body
    .split(/(`[^`\n]*`)/)
    .map((part, index) => {
      if (index % 2 === 1) return part; // inline code stays untouched
      return part.replace(SECTION_LINK_REGEX, (raw, id: string) => {
        const target = doc.byId[id];
        if (!target) return raw;
        return `[${target.title.replace(/([[\]])/g, '\\$1')}](${SECTION_HREF_PREFIX}${id})`;
      });
    })
    .join('');

interface MarkdownBodyProps {
  body: string;
  doc: PlaybookDoc;
  onNavigate: (id: string) => void;
}

const MarkdownBody = ({ body, doc, onNavigate }: MarkdownBodyProps) => {
  const source = useMemo(() => linkifySectionRefs(body, doc), [body, doc]);

  return (
    <div className={styles.markdown}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => {
            if (href?.startsWith(SECTION_HREF_PREFIX)) {
              const id = href.slice(SECTION_HREF_PREFIX.length);
              return (
                <button type='button' className={styles.sectionLink} onClick={() => onNavigate(id)}>
                  {children}
                </button>
              );
            }
            return (
              <a href={href} target='_blank' rel='noreferrer'>
                {children}
              </a>
            );
          },
          table: ({ children }) => (
            <div className={styles.tableWrap}>
              <table>{children}</table>
            </div>
          ),
        }}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
};

interface Props {
  doc: PlaybookDoc;
  lang: PlaybookLang;
  /** Top-level section to show, together with all of its sub sections. */
  rootId: string;
  highlightId?: string | null;
  onNavigate: (id: string) => void;
  onCopyLink: (id: string) => void;
}

interface SectionBlockProps {
  section: PlaybookSection;
  doc: PlaybookDoc;
  lang: PlaybookLang;
  highlightId?: string | null;
  onNavigate: (id: string) => void;
  onCopyLink: (id: string) => void;
}

/** Heading + markdown body of one section. Sub-sections render inside via `children`. */
const SectionBlock = ({
  section,
  doc,
  lang,
  highlightId,
  onNavigate,
  onCopyLink,
  children,
}: React.PropsWithChildren<SectionBlockProps>) => {
  const Heading = `h${section.level}` as 'h3' | 'h4';
  return (
    <section
      id={anchorDomId(section.id)}
      className={`${styles.section} ${styles[`level${section.level}`]} ${
        highlightId === section.id ? styles.highlight : ''
      }`}
    >
      <div className={`${styles.heading} flex items-center gap-2`}>
        <Heading className={styles.title}>{section.title}</Heading>
        <Tooltip title={tr('copyLink', lang)}>
          <Button
            type='text'
            size='small'
            className={styles.copyButton}
            aria-label={tr('copyLink', lang)}
            icon={<LinkOutlined />}
            onClick={() => onCopyLink(section.id)}
          />
        </Tooltip>
      </div>
      {section.body && <MarkdownBody body={section.body} doc={doc} onNavigate={onNavigate} />}
      {children}
    </section>
  );
};

const PlaybookContent = ({ doc, lang, rootId, highlightId, onNavigate, onCopyLink }: Props) => {
  const { root, groups } = useMemo(() => {
    const all = doc.sections.filter((s) => s.rootId === rootId);
    const rootSection = all.find((s) => s.level === 2);
    const level3 = all.filter((s) => s.level === 3);
    return {
      root: rootSection,
      groups: level3.map((s) => ({ section: s, children: all.filter((c) => c.level === 4 && c.parentId === s.id) })),
    };
  }, [doc, rootId]);

  const shared = { doc, lang, highlightId, onNavigate, onCopyLink };

  return (
    <div className={styles.content}>
      {/* The top-level title is already shown in the panel header, so only its intro text is rendered here. */}
      {root?.body && (
        <div id={anchorDomId(root.id)} className={styles.intro}>
          <MarkdownBody body={root.body} doc={doc} onNavigate={onNavigate} />
        </div>
      )}
      {groups.map(({ section, children }) => (
        <SectionBlock key={section.id} section={section} {...shared}>
          {children.map((child) => (
            <SectionBlock key={child.id} section={child} {...shared} />
          ))}
        </SectionBlock>
      ))}
    </div>
  );
};

export default PlaybookContent;
